/**
 * Checkout helpers for cart ↔ draft order linking.
 *
 * After Confirm & Pay, GET /carts/:id may return order_id + order_number.
 * Resume mode: line items from cart, everything else from GET /orders/:id.
 */
import { readResolveTaxable } from "orderResolve/utils/readResolveTaxable";

const NULL_OBJECT_ID = "000000000000000000000000";

/** Ignore empty ids and Mongo null-object-id placeholders from cart GET. */
export function isValidLinkedOrderId(id: unknown): boolean {
  const value = String(id ?? "").trim();
  if (!value) return false;
  if (value === NULL_OBJECT_ID) return false;
  if (value.length === 24 && /^0+$/.test(value)) return false;
  return true;
}

export function getResumeOrderIdFromCart(cart: unknown): string | null {
  const id = String((cart as { order_id?: string })?.order_id ?? "").trim();
  return isValidLinkedOrderId(id) ? id : null;
}

export function getResumeOrderNumberFromCart(cart: unknown): string | null {
  if (!getResumeOrderIdFromCart(cart)) return null;
  const number = String((cart as { order_number?: string })?.order_number ?? "").trim();
  return number || null;
}

/** Order API responses use Mongo `_id` as the canonical id. */
export function getOrderIdFromResponse(order: unknown): string {
  const data = order as { _id?: string; id?: string; order_id?: string };
  const id = String(data?._id ?? data?.id ?? data?.order_id ?? "").trim();
  return isValidLinkedOrderId(id) ? id : "";
}

export function getOrderNumberFromResponse(order: unknown): string {
  const data = order as { order_number?: string };
  const orderId = getOrderIdFromResponse(order);
  return String(data?.order_number ?? orderId).trim();
}

export function getLineItemsFromCart(cart: unknown): any[] {
  const items = (cart as { cart_items?: unknown[] })?.cart_items;
  return Array.isArray(items) ? items : [];
}

export function buildCartHeaderFromCart(cart: unknown) {
  const data = cart as Record<string, unknown>;
  const orderCharges = (data.order_charges ?? { shipping_option: [], specialfee: [] }) as {
    shipping_option?: unknown[];
    specialfee?: unknown[];
  };

  return {
    discount_amount: Number(data.discount_amount || 0),
    discount_code: String(data.discount_code || ""),
    subtotal_amount: Number(data.subtotal_amount || 0),
    total_amount: Number(data.total_amount || 0),
    order_charges: orderCharges,
    order_tax_summary: data.order_tax_summary ?? null,
    selected_fee_ids: Array.isArray(data.selected_fee_ids)
      ? data.selected_fee_ids
      : [],
  };
}

/** Build per-line coupon map from cart rows (create flow only). */
export function buildPerItemCouponMapFromCartItems(cartItems: any[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const item of cartItems) {
    const key = `${item.product_id}:${item.variant_id}`;
    const rowDisc = Number(item.discount_on_total_quantity || 0);
    if (rowDisc > 0) map[key] = Math.round(rowDisc * 100) / 100;
  }
  return map;
}

/** Order charges live at order_charges[0] on sales orders. */
export function getOrderChargesBlock(order: unknown): Record<string, unknown> {
  const charges = (order as { order_charges?: unknown })?.order_charges;
  if (Array.isArray(charges) && charges[0] && typeof charges[0] === "object") {
    return charges[0] as Record<string, unknown>;
  }
  if (charges && typeof charges === "object" && !Array.isArray(charges)) {
    return charges as Record<string, unknown>;
  }
  return {};
}

export type InitialShippingSelection = {
  manual_shipping_id: string | null;
  ups_service_code: string | null;
  integration_id: string | null;
  shipper_address_id: string | null;
};

export function getInitialShippingSelectionFromOrder(
  order: unknown
): InitialShippingSelection | null {
  const shippingRows = getOrderChargesBlock(order).shipping_option;
  const firstRow = Array.isArray(shippingRows) ? shippingRows[0] : null;
  if (!firstRow || typeof firstRow !== "object") return null;

  const row = firstRow as Record<string, unknown>;
  const rawId = String(row.shipping_id ?? row.shipping__id ?? "").trim();
  if (!rawId) return null;

  const typeUp = String(row.shipping_type ?? "").trim().toUpperCase();
  const isUps =
    typeUp === "UPS" ||
    (/^[0-9A-Z]{2,3}$/.test(rawId) && rawId.length <= 3 && rawId !== "custom-manual");
  return {
    manual_shipping_id: isUps ? null : rawId,
    ups_service_code: isUps ? rawId : null,
    integration_id: null,
    shipper_address_id: null,
  };
}

/** Map saved order shipping rows for ShippingOptionsui savedShippingRows prop. */
export function mapOrderShippingRowsForPicker(order: unknown): any[] | null {
  const shippingRows = getOrderChargesBlock(order).shipping_option;
  if (!Array.isArray(shippingRows) || !shippingRows.length) return null;

  const mapped = shippingRows.map((r: any) => ({
    shipping__id: String(r.shipping_id ?? r.shipping__id ?? ""),
    shipping_name: String(r.shipping_name ?? ""),
    shipping_type: String(r.shipping_type ?? ""),
    is_customer_pickup: r.is_customer_pickup === true,
    shipping_amount: Number(r.shipping_amount ?? 0),
    cost: Number(r.cost ?? r.shipping_amount ?? 0),
    shipping_tax_percentage: Number(r.shipping_tax_percentage ?? 0),
    shipping_tax_amount: Number(r.shipping_tax_amount ?? 0),
    shipping_taxable: readResolveTaxable(r, ["shipping_taxable", "taxable", "is_taxable"], true),
    shipping_delivarydate: String(r.shipping_delivarydate ?? r.shipping_delivery_date ?? ""),
    shipping_delivery_time: String(r.shipping_delivery_time ?? ""),
  }));

  const hasCustomManual = mapped.some((r) => r.shipping__id === "custom-manual");
  return hasCustomManual ? null : mapped;
}

export function getInitialSpecialFeeIdsFromOrder(order: unknown): string[] | undefined {
  const feeRows = getOrderChargesBlock(order).specialfee;
  if (!Array.isArray(feeRows) || !feeRows.length) return undefined;

  const ids = feeRows
    .map((r: any) => String(r?.specialfee_id || r?.specialfee_name || ""))
    .filter(Boolean);
  return ids.length ? ids : undefined;
}

export function getSpecialFeeRowsFromOrder(order: unknown): any[] {
  const feeRows = getOrderChargesBlock(order).specialfee;
  return Array.isArray(feeRows) ? feeRows : [];
}

export function getShippingRowsFromOrder(order: unknown): any[] {
  const shipRows = getOrderChargesBlock(order).shipping_option;
  return Array.isArray(shipRows) ? shipRows : [];
}

/** Link cart to a newly created draft order (create flow only). */
export async function linkCartToDraftOrder(params: {
  cartId: string | null | undefined;
  orderId: string;
  orderNumber: string;
  token: string | null | undefined;
}): Promise<void> {
  const cartId = String(params.cartId ?? "").trim();
  const orderId = String(params.orderId ?? "").trim();
  const orderNumber = String(params.orderNumber ?? "").trim();
  const token = params.token;

  if (!cartId || !isValidLinkedOrderId(orderId) || !token) return;

  const apiUrl = process.env.REACT_APP_API_URL;
  if (!apiUrl) throw new Error("API URL is not configured");

  const response = await fetch(`${apiUrl}/carts/${cartId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      order_id: orderId,
      order_number: orderNumber || orderId,
    }),
  });

  if (!response.ok) {
    const message = await response.text().catch(() => "");
    throw new Error(message || `Failed to link cart ${cartId} to order ${orderId}`);
  }
}
